/* © CNES */
import { useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Image } from 'primereact/image';
import { Tooltip } from 'primereact/tooltip';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { restoreArchivedProduct } from '../../../api/cart/Cart';
import { getItemDetails } from '../../../api/items/Items';
import { ConfigContext } from '../../../context/ConfigContext';
import { useFormattedCollections } from '../../../hooks/CustomHooks';
import { CollectionDetails } from '../../../models/CollectionDetails';
import { ItemsAvailabilities } from '../../../models/ItemAvailability';
import { ProductsAdded } from '../../../models/ProductsAdded';
import placeholder_quicklook from '../../../placeholder_quicklook.png';
import {
  isItemAvailable,
  replaceCatalogPathForQuicklook,
} from '../../shared/Utils';
import { ItemDetails } from './ItemDetails/ItemDetails';
import './ItemsData.scss';

/**
 * Items Data Props.
 */
export interface ItemsDataProps {
  productsAdded: ProductsAdded;
  selectedItems?: string[];
  setSelectedItems?: Function;
  isArchived?: boolean;
  itemsAvailabilitySummary?: {
    productId: string;
    available: boolean | undefined;
  }[];
  refetchGetSavedProduct?: Function;
  itemAvailabilities?: ItemsAvailabilities | undefined;
}

/**
 * Items Data displays cards for each product.
 * @param productsAdded all products added by logged in user.
 * @param selectedItems the selected products by user.
 * @param setSelectedItems Function to set selected items.
 * @param isArchived boolean that define if product is archived or not
 * @param itemsAvailabilitySummary items Availability Summary
 * @param refetchGetSavedProduct refetch GetSavedProduct
 * @returns Element.
 */
export const ItemsData = ({
  productsAdded,
  selectedItems,
  setSelectedItems,
  isArchived,
  itemsAvailabilitySummary,
  refetchGetSavedProduct,
  itemAvailabilities,
}: ItemsDataProps) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState<boolean[]>(
    Array(productsAdded?.products?.length).fill(false)
  );
  const productsIndex =
    productsAdded?.products && Object.keys(productsAdded?.products);
  const context = useContext(ConfigContext);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [productNameToRestore, setProductNameToRestore] = useState<string>('');

  /**
   * UseQuery synthax to call getItemDetails from regards.
   * product name is required to fetch its details
   * @param productName for the request body
   * @returns data: product details.
   */
  const { refetch, data } = useQuery({
    queryKey: [
      'GetItemDetails',
      {
        productName: selectedProduct,
      },
      navigate,
      context.setError400,
    ],
    queryFn: getItemDetails,
    enabled: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  /**
   * format selected collection data from item search with storage
   */
  const selectedCollectionDetails: CollectionDetails[] =
    useFormattedCollections(data?.features, itemAvailabilities);

  const { isSuccess: isRestoreSuccess } = useQuery({
    queryKey: [
      'RestoreArchivedProduct',
      {
        payload: {
          product: productNameToRestore,
          process: 'download',
        },
      },
    ],
    queryFn: restoreArchivedProduct,
    enabled: !!productNameToRestore,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  useEffect(() => {
    if (isRestoreSuccess) {
      refetchGetSavedProduct && refetchGetSavedProduct();
    }
    // eslint-disable-next-line
  }, [isRestoreSuccess]);

  /**
   * onItemChange add or remove product on checkbox click.
   * @param e click event on checkbox.
   */
  const onItemChange = (e: any) => {
    if (selectedItems) {
      let _selectedItems = [...selectedItems];
      if (e.checked) {
        _selectedItems.push(e.value);
      } else {
        _selectedItems = _selectedItems.filter((item) => item !== e.value);
      }
      setSelectedItems && setSelectedItems(_selectedItems);
    }
  };

  /**
   * handle Deselect All Items.
   */
  const handleDeselectAllItems = () => {
    setSelectedItems && setSelectedItems([]);
  };
  /**
   * handle Select All Items.
   */
  const handleSelectAllItems = () => {
    let _selectedItems = [...productsAdded?.products];
    setSelectedItems && setSelectedItems(_selectedItems);
  };

  /**
   * handle Open Details
   * @param index of the product to display details from.
   * @param isOpen is the dialog open.
   * @param productName is the title of the product
   * @param e click event on button.
   */
  const handleOpenDetails = (
    index: number,
    isOpen: boolean,
    productName: string,
    e?: any
  ) => {
    e?.stopPropagation();
    let visibleTable = [...visible];
    visibleTable[index] = isOpen;
    setVisible(visibleTable);
    // the "collectionName" is used as a reference to check if the detail object is completely filled or not
    const productId = context.productsAdded.collectionsDetails.find(
      (collection) => collection.productName === productName
    )?.collectionName;
    if (isOpen && !productId) {
      setSelectedProduct(productName);
    }
  };

  /**
   * Trigger GetItemDetails query
   */
  useEffect(() => {
    if (selectedProduct) {
      void refetch();
    }
    // eslint-disable-next-line
  }, [selectedProduct]);

  /**
   * Update ProductsAdded context for selected product
   * @requires data
   * @requires context.productsAdded
   */
  useEffect(() => {
    if (selectedCollectionDetails?.length) {
      // previous context
      let productsAddedToUpdate: ProductsAdded = { ...context.productsAdded };
      let collectionsDetailsToUpdate = [
        ...context.productsAdded.collectionsDetails,
      ];
      const index = collectionsDetailsToUpdate.findIndex(
        (element) =>
          element.productName === selectedCollectionDetails[0].productName
      );
      if (index !== -1) {
        collectionsDetailsToUpdate[index] = selectedCollectionDetails[0];
        productsAddedToUpdate.collectionsDetails = collectionsDetailsToUpdate;

        context.setProductsAdded(productsAddedToUpdate);
      }
    }
    // eslint-disable-next-line
  }, [selectedCollectionDetails]);

  const isSelected = (
    selectedItems: string[] | undefined,
    productName: string
  ) => selectedItems?.includes(productName);

  const handleRestoreClick = (productName: string, e: any) => {
    e?.stopPropagation();
    setProductNameToRestore(productName);
  };

  return (
    <div className="ItemsDataContainer">
      {!!productsAdded?.products?.length && !isArchived && (
        <div className="SelectButtonsContainer">
          <Button
            label="Select all"
            className="buttonSelectAll"
            onClick={handleSelectAllItems}
            link
          />
          <Button label="Deselect all" onClick={handleDeselectAllItems} link />
        </div>
      )}

      <div>
        {productsAdded.collectionsDetails?.map(
          ({ productName, quicklook, id }, index) => {
            return (
              <div key={productsIndex[index]} className="cardItems">
                <Card>
                  <div className="box">
                    {!isArchived && selectedItems && (
                      <div className="checkbox">
                        <Checkbox
                          aria-label="checkbox"
                          inputId={productsIndex[index]}
                          value={productName}
                          onChange={onItemChange}
                          checked={
                            isSelected(selectedItems, productName) || false
                          }
                        />
                      </div>
                    )}
                    <div
                      className={`item ${
                        isSelected(selectedItems, productName) && 'checked'
                      } ${
                        !isItemAvailable(itemsAvailabilitySummary, id) &&
                        !isArchived &&
                        'unavailable'
                      }`}
                      {...(!isArchived &&
                        selectedItems && {
                          onClick: () =>
                            onItemChange({
                              value: productName,
                              checked: !isSelected(selectedItems, productName),
                            }),
                        })}
                    >
                      <div className="items-image">
                        <Image
                          src={replaceCatalogPathForQuicklook(quicklook)}
                          onError={(e) => {
                            if (
                              (e?.target as HTMLImageElement).src !==
                              placeholder_quicklook
                            ) {
                              (e?.target as HTMLImageElement).src =
                                placeholder_quicklook;
                            }
                          }}
                          alt="placeholder_quicklook"
                          height="100%"
                          width="100%"
                          max-width="100%"
                          min-width="100%"
                        />
                      </div>
                      <div
                        className={`title ${
                          isSelected(selectedItems, productName) && 'checked'
                        }`}
                      >
                        <p>{productName}</p>
                      </div>
                      <div className="item-action">
                        <Button
                          rounded
                          text
                          aria-label="fileSearch"
                          onClick={(e) =>
                            handleOpenDetails(index, true, productName, e)
                          }
                        >
                          <img
                            alt="fileSearch"
                            src={require('./filesearch.png')}
                          />
                        </Button>
                        {!isItemAvailable(itemsAvailabilitySummary, id) &&
                          !isArchived && (
                            <Button
                              aria-label="restoreButton"
                              onClick={(e: React.MouseEvent) =>
                                handleRestoreClick(productName, e)
                              }
                              icon={
                                <i
                                  className="pi pi-cloud-download"
                                  style={{
                                    fontSize: '25px',
                                  }}
                                />
                              }
                              rounded
                              text
                              severity="warning"
                              tooltipOptions={{ position: 'bottom' }}
                              tooltip="Restore"
                            />
                          )}
                      </div>
                    </div>
                    {!isItemAvailable(itemsAvailabilitySummary, id) &&
                      !isArchived && (
                        <div className="warning">
                          <i
                            className="pi pi-exclamation-triangle datalakeIcon"
                            style={{ color: '#EF4A11', fontSize: '25px' }}
                          />
                          <Tooltip
                            target=".datalakeIcon"
                            content="Product needs to be restored"
                            position={'bottom'}
                          />
                        </div>
                      )}
                  </div>
                </Card>

                <Dialog
                  header={productName}
                  visible={visible[index]}
                  style={{ width: '75vw' }}
                  headerStyle={{ color: 'rgba(0, 45, 128, 1)' }}
                  contentStyle={{ padding: '0px 7em' }}
                  onHide={() => handleOpenDetails(index, false, productName)}
                >
                  {
                    <ItemDetails
                      collectionDetails={context.productsAdded.collectionsDetails?.find(
                        (collection) => collection.productName === productName
                      )}
                    />
                  }
                </Dialog>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};
